import { Injectable, inject } from '@angular/core';

import { StatisticsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, LeagueItem, RegionItem, isVirtualCompetitionGroupItem } from '@frontend/sports/common/core/data-access/sport-model';
import { Omit, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { LeagueGroup } from '@frontend/sports/grid/core/feature/model';
import { flatten } from 'lodash-es';

export interface GetStandingsParams {
    sport: CountItem;
    selectedVirtualGroupId?: number;
    expandedGroups?: number;
}
@Injectable({ providedIn: 'root' })
export class TeamPagesStandingService {
    private statisticsConfig = inject(StatisticsConfig);

    getStandingsGroups(params: GetStandingsParams): LeagueGroup[] {
        const { sport, selectedVirtualGroupId, expandedGroups = this.statisticsConfig.expandedLeagueTablesCount } = params;

        if (sport.id !== SportConstant.Soccer) {
            return [];
        }

        const leagueGroups = !selectedVirtualGroupId
            ? this.getStandingsForCompetitions(sport)
            : this.getStandingsForVirtualGroups(sport, selectedVirtualGroupId);

        leagueGroups.forEach((leagueGroup, index) => (leagueGroup.collapsed = index >= expandedGroups));

        return leagueGroups;
    }

    private getStandingsForCompetitions(sport: CountItem): LeagueGroup[] {
        const leagueGroups = flatten(
            (<RegionItem[]>sport.children).map((region) =>
                flatten(
                    (<LeagueItem[]>region.children).map((leagueItem) =>
                        leagueItem.isVirtual ? this.mapVirtualCompetition(leagueItem, region) : this.mapCompetition(leagueItem, region),
                    ),
                ),
            ),
        );

        return leagueGroups;
    }

    private getStandingsForVirtualGroups(sport: CountItem, selectedVirtualGroupId: number): LeagueGroup[] {
        const leagueGroups = flatten(
            (<RegionItem[]>sport.children).map((region) =>
                flatten((<LeagueItem[]>region.children).map((leagueItem) => this.mapVirtualGroups(leagueItem, region, selectedVirtualGroupId))),
            ),
        );

        return leagueGroups;
    }

    private mapCompetition(competition: LeagueItem, region: RegionItem): LeagueGroup[] {
        if (!competition.standings) {
            return [];
        }

        return [{ ...this.toLeagueGroup(competition, region), id: competition.id, name: competition.name, canBeFavourited: true }];
    }

    private mapVirtualCompetition(competition: LeagueItem, region: RegionItem): LeagueGroup[] {
        const standings: LeagueGroup[] = [];

        if (competition.standings) {
            standings.push({
                ...this.toLeagueGroup(competition, region),
                id: competition.siblings[0],
                name: competition.name,
                canBeFavourited: true,
            });
        }

        return [...standings, ...this.mapVirtualGroups(competition, region)];
    }

    private mapVirtualGroups(competition: LeagueItem, region: RegionItem, selectedVirtualGroup?: number): LeagueGroup[] {
        if (!competition.isVirtual || !competition.children) {
            return [];
        }

        const leagueGroups = competition.children
            .filter(isVirtualCompetitionGroupItem)
            .filter((virtualGroup) => virtualGroup.standings && (!isDefined(selectedVirtualGroup) || virtualGroup.id === selectedVirtualGroup))
            .map<LeagueGroup>((virtualGroup) => ({
                ...this.toLeagueGroup(virtualGroup, region),
                id: virtualGroup.siblings[0],
                name: `${competition.name} - ${virtualGroup.name}`,
                canBeFavourited: !(virtualGroup.stageIds.length || virtualGroup.groupIds.length),
            }));

        return leagueGroups;
    }

    private toLeagueGroup(leagueItem: LeagueItem, region: RegionItem): Omit<LeagueGroup, 'id' | 'name' | 'canBeFavourited'> {
        return {
            count: leagueItem.counts.preMatch + leagueItem.counts.live,
            events: [],
            collapsed: false,
            collapsible: true,
            deferred: false,
            siblings: [leagueItem.id],
            region,
        };
    }
}
